/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  DictionaryService,
  FormState,
  getRouteParam,
  globalDictionaries,
  InitViewService,
  REGION_AU,
  REGION_NZ,
} from '@gh/common-shared';
import { ActivatedRouteSnapshot } from '@angular/router';
import { AutocompleteItem, DirtyType, ID } from '@gh/common-shared-data';
import { Observable } from 'rxjs';
import { map$ } from '@gh/rx/operators';
import {
  AutoSaveMin,
  BaseDesignRestService,
  BuilderPermission,
  ContactDeliveryMethod,
  HlpDesignImage,
  HlpDetails,
  HlpDetailsDto,
  HlpRestService,
  HlpStatus,
  LandAreaUom,
  ListingStatus,
  PriceBookRestService,
  PriceBookUsage,
  QuotationBase,
  SiteRestService,
  Storeys,
  SystemRoleBit,
  UserRestService,
} from '@gh/builder-shared-data';
import { FilteredDataQuery, getSlicedData, SelectionModel } from '@gh/core-data';
import { IMAGE_TYPES } from './hlp.model';
import { GridLayoutService } from '@gh/core-ui-grid';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  quoteDetailsInitFromDto,
  QuoteDetailsInitResponse,
} from '../../leads/resources/quote.model';
import { FormMode, loadTinymce } from '@gh/core-ui';
import { HlpQuoteDetailsMasterInfo } from './hlp-quote.model';
import { REGION_CODE } from '@gh/config';
import {
  queryQuoteHasErrors,
  queryQuoteToHlpModelWithVariations,
} from '../components/hlp-details/hlp-details-quote-tab/hlp-quote-details.state';
import { getLocalValue, sortByStringField } from '@gh/core-util';
import { omit, pick } from 'lodash';

const commonTabFields: string[] = ['salesperson', 'alternateSalesperson', 'displayPrice', 'isPublishedOnDomainComAu',
  'isPublishedOnRealestateComAu', 'publishedById', 'publishedDate', 'publishedByName', 'listingStatus', 'siteName',
  'siteStreet', 'siteSuburb', 'sitePostcode', 'siteAddress', 'siteLotNumber', 'siteStreetNumber', 'siteState','latitude', 'longitude', 'memo',
  'listingStatus',
];
const designTabFields: string[] = ['baseDesign', 'designSeriesName', 'images', 'websiteDescription', 'websiteDescriptionText',
  'bedrooms', 'bathrooms', 'carSpaces', 'floorArea', 'floorAreaUom', 'landArea', 'landAreaUom', 'storeys', 'features',
  'additionalFeatures',
];

@Injectable()
export class HlpDetailsBusinessService implements InitViewService {
  masterInfoForm?: FormState<HlpQuoteDetailsMasterInfo>;
  mainForm: FormState<HlpDetails>;
  quoteForm: any;
  quoteVariationSelectionModel: Map<string, SelectionModel> = new Map();
  tinymce: any;
  hlpDetails: HlpDetails;
  isShowQuoteTab: boolean;
  needValidate: boolean;
  masterInfoFormChangeDetectorRef: any;
  onProcess: any;
  isCloned: boolean;
  snapshotVariations: any[];

  get isCurrentUserSupportsProfit(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasSystemRole(SystemRoleBit.Franchisee);
  }

  constructor(
    private hlpRest: HlpRestService,
    private userRest: UserRestService,
    private designRest: BaseDesignRestService,
    private priceBookRest: PriceBookRestService,
    private dictionaryService: DictionaryService,
    private siteRest: SiteRestService,
    private userContext: BuilderUserContext,
    private gridLayoutService: GridLayoutService,
  ) {
    this.isShowQuoteTab = true;
  }

  initView(route: ActivatedRouteSnapshot): any {
    const id = getRouteParam(route, 'hlpId');
    const tab = route.queryParams.tab;
    const cloneId = route.queryParams.clonedHlp;
    const supportsProfit = this.isCurrentUserSupportsProfit;
    let details;
    this.isCloned = false;
    if (cloneId) {
      this.isCloned = true;
      const clonedData = getLocalValue<HlpDetailsDto | undefined>('clonedHlp', undefined);
      if (clonedData)  details = this.hlpDetailsFromDto(clonedData);
      window.localStorage.removeItem('clonedHlp');
    } else if (id) {
      details = this.get(getRouteParam(route, 'hlpId'));
    } else {
      details = { ...this.hlpDetailsFromDto(this.getNewDetails()) };
    }
    return {
      details,
      listingStatuses    : this.dictionaryService.getByEnum(ListingStatus),
      states             : this.dictionaryService.get(globalDictionaries.states),
      storeys            : this.dictionaryService.getByEnum(Storeys),
      landAreaUom        : this.dictionaryService.getTextDictionaryFromGlobal(globalDictionaries.LandAreaUom).pipe(
        map$((res) => {
          res.seq().forEach((item) => item.id = item.id.toLowerCase());
          return res;
        })),
      autoSaveMin        : this.dictionaryService.getByEnum(AutoSaveMin),
      quotationBase      : this.dictionaryService.getByEnum(QuotationBase),
      variationGridLayout: this.gridLayoutService.get(supportsProfit ? 'variationsWithProfit' : 'variations'),
      tinymce            : loadTinymce(),
      tabName            : tab,
    };
  }

  get(id: ID): Observable<HlpDetails> {
    return this.hlpRest.get(id).pipe(map$(this.hlpDetailsFromDto));
  }

  getHlpInfo(id: ID): Observable<any> {
    return this.hlpRest.getHlpInfo(id);
  }

  hlpQuoteDetailsInitFromHlpDetailsDto(details: HlpDetails): any {
    return {
      variations       : details.variations,
      deliveryBy       : ContactDeliveryMethod.Email,
      description      : details.quoteDescription,
      priceBookId      : details.priceBookId,
      priceBookName    : details.priceBookName,
      siteAddress      : details.commonDetails.siteAddress,
      siteName         : details.commonDetails.siteName,
      housePlanId      : details.housePlanId,
      housePlanCode    : details.housePlanCode,
      housePlanName    : details.housePlanName,
      baseHousePlanId  : details.baseHousePlanId,
      baseHousePlanCode: details.baseHousePlanCode,
      baseHousePlanName: details.baseHousePlanName,
      memo             : details.commonDetails.memo,
      created          : '',
      base             : details.base,
      isMemoOnTop      : false,
      pbSalesPrice     : parseFloat(details.pbSalesPrice), // PRICE_1
      pbPrice          : details.pbPrice, //  PRICE_2
      debit            : details.debit, // DEBIT_TOTAL
      credit           : details.credit, // CREDIT_TOTAL
      profit           : details.profit, // PROFIT_TOTAL
      rfqPrice1        : 0, // QUOTE_TOTAL_1
      rfqPrice2        : 0, // QUOTE_TOTAL_2
      pbCost           : details.pbCost,

      origSalesTax     : parseFloat(details.origSalesTax),
      adjSalesTax      : parseFloat(details.adjSalesTax),
      totalSalesTax    : parseFloat(details.totalSalesTax),
      totalPriceWithGst: details.totalPriceWithGst,

      isSalesEstimateShown: false,
      isQuickQuotesAllowed: true,
      isGtoTopExist       : true,
      useSalesEstimate    : false,
      isCompletionQuote   : true,
      isRounded           : details.isRounded,

      salesTaxRates: details.salesTaxRates,
    };
  }

  findSalesPersonsByName(query: FilteredDataQuery<string>, filter: any): Observable<any[]> {
    return this.userRest.findSalespersonsAsDataSource({
                                                        ...filter,
                                                        searchString: query.filter,
                                                      }).select(query).pipe(map$(getSlicedData));
  }

  findDesignsByName(query: FilteredDataQuery<string>, filter: any): Observable<any[]> {
    return this.designRest.findAllAsDataSource({
                                                 ...filter,
                                                 searchString: query.filter,
                                               }).select(query).pipe(map$(getSlicedData));
  }

  update(data: HlpDetails): Observable<HlpDetails> {
    return this.hlpRest.update(this.hlpDetailsToDto(data)).pipe(map$(this.hlpDetailsFromDto));
  }

  create(data: HlpDetails): Observable<any> {
    return this.hlpRest.create(this.hlpDetailsToDto(data));
  }

  findPriceBooksByName(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.priceBookRest.findAllAsAutocompleteDataSource({
                                                                searchString: query.filter,
                                                                usage       : [PriceBookUsage.Normal],
                                                              }).select(query).pipe(
      map$(getSlicedData));
  }

  getFormMode(details: HlpDetails): number {
    if (details.status === HlpStatus.Deleted) {
      return FormMode.ViewMode;
    }
    if (!details.houseAndLandPackageId
      && (
        this.userContext.hasPermission(BuilderPermission.Sales.Hlp.CREATE) ||
        this.userContext.hasSystemRole(SystemRoleBit.Corporate) ||
        this.userContext.hasSystemRole(SystemRoleBit.Master)
      )) {
      return FormMode.CreateMode;
    }
    if (
      this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT) ||
      this.userContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.userContext.hasSystemRole(SystemRoleBit.Master)
    ) {
      return FormMode.EditMode;
    }
    return FormMode.ViewMode;
  }

  calculate(data: HlpDetails): Observable<{ saveResponse: QuoteDetailsInitResponse; response: HlpDetailsDto }> {
    return this.hlpRest.recalculate(this.hlpDetailsToDto(data)).pipe(map$((response) => (
      { saveResponse: quoteDetailsInitFromDto(response), response }
    )));
  }

  publish(id: ID): Observable<HlpDetails> {
    return this.hlpRest.publish(id).pipe(map$(this.hlpDetailsFromDto));
  }

  unPublish(id: ID): Observable<HlpDetails> {
    return this.hlpRest.unPublish(id).pipe(map$(this.hlpDetailsFromDto));
  }

  getNewDetails(): any {
    const salesPerson = {
      id  : '',
      name: '',
    };
    if (this.userContext.hasSystemRole(SystemRoleBit.SalesPerson)) {
      salesPerson.id = this.userContext.data.id;
      salesPerson.name = this.userContext.data.fullName;
    }
    return {
      houseAndLandPackageId       : '',
      hlpNumber                   : '',
      housePlanId                 : '',
      externalId                  : '',
      base                        : '',
      memo                        : '',
      debit                       : undefined,
      credit                      : undefined,
      profit                      : undefined,
      status                      : HlpStatus.Draft,
      pbPrice                     : undefined,
      storeys                     : undefined,
      version                     : undefined,
      bedrooms                    : undefined,
      bathrooms                   : undefined,
      carSpaces                   : undefined,
      floorArea                   : undefined,
      floorAreaUom                : undefined,
      landArea                    : undefined,
      landAreaUom                 : [REGION_AU, REGION_NZ].includes(REGION_CODE) ? LandAreaUom.sqm : LandAreaUom.sqft,
      images                      : [],
      totalPrice1                 : '',
      totalPrice2                 : '',
      displayPrice                : '',
      priceBookId                 : '',
      priceBookName               : '',
      housePlanCode               : '',
      housePlanName               : '',
      siteName                    : '',
      siteStreet                  : '',
      siteSuburb                  : '',
      sitePostcode                : '',
      siteAddress                 : '',
      siteLotNumber               : undefined,
      siteStreetNumber            : undefined,
      siteState                   : '',
      latitude                    : '',
      longitude                   : '',
      adjSalesTax                 : '',
      baseDesignId                : '',
      origSalesTax                : '',
      pbSalesPrice                : '',
      totalSalesTax               : '',
      salespersonId               : salesPerson.id,
      salespersonName             : salesPerson.name,
      alternateSalespersonId      : '',
      alternateSalespersonName    : '',
      baseDesignName              : '',
      baseHousePlanId             : '',
      designSeriesName            : '',
      baseHousePlanCode           : '',
      baseHousePlanName           : '',
      features                    : [],
      additionalFeatures          : [],
      salesTaxRates               : [],
      variations                  : [],
      listingStatus               : ListingStatus.new,
      websiteDescription          : '',
      quoteDescription            : '',
      origSalesTaxPct             : undefined,
      publishedById               : undefined,
      publishedDate               : undefined,
      publishedByName             : '',
      isRounded                   : false,
      isDisplayPriceOverridden    : false,
      isPublishedOnDomainComAu    : false,
      isPublishedOnRealestateComAu: false,
      isSiteDetailsLocked         : false,
    };
  }

  isAllValid(): boolean {
    const quotRequiredFields = ['quoteDescription'];
    const data = this.prepearData();
    const quoteDataIsPresent = !quotRequiredFields.find((fieldName: string) => !data[fieldName]);
    this.needValidate = true;
    const hasSelectedFacade = data.designDetails.facades.find((facade: HlpDesignImage) => facade.isSelected);
    return !this.mainForm.invalid
      && hasSelectedFacade
      && ((!this.quoteForm && quoteDataIsPresent) || (this.quoteForm && !this.quoteForm.state.project(
        queryQuoteHasErrors)))
      && (!this.masterInfoForm || !this.masterInfoForm.invalid);
  }

  prepearData(): HlpDetails {
    const result = {
      ...this.hlpDetails,
      ...this.mainForm.submitValue,
      commonDetails: {
        ...this.hlpDetails.commonDetails,
        ...this.mainForm.submitValue.commonDetails,
      },
    } as HlpDetails;
    result.designDetails.additionalFeatures = result.designDetails.additionalFeatures.filter((item) => !!item);
    if (!this.quoteForm) {
      return result;
    }

    if (this.getFormMode(this.hlpDetails) === FormMode.CreateMode && !result.commonDetails.displayPrice) {
      result.commonDetails.displayPrice = '0';
    }
    const oldVariations = this.snapshotVariations;
    const quotation = this.quoteForm.state.project(queryQuoteToHlpModelWithVariations);
    // mark as New variations if HLP was cloned
    if (this.isCloned) {
      oldVariations.forEach((oldVariation) => {
        if (!oldVariation.dirtyType) {
          quotation.variations.push({
                                      ...oldVariation,
                                      dirtyType: DirtyType.New,
                                    });
        }
      });
    }

    // remove variations if main quotation info was changed
    if (result.priceBookId !== quotation.priceBookId ||
      result.base !== quotation.base ||
      result.baseHousePlanId !== quotation.baseHousePlanId) {
      oldVariations.forEach((oldVariation) => {
        quotation.variations.push({
                                    ...oldVariation,
                                    dirtyType: DirtyType.Removed,
                                  });
      });
    }
    return {
      ...result,
      ...quotation,
    };
  }

  hlpDetailsFromDto(dto: HlpDetailsDto): HlpDetails {
    return {
      ...omit(dto, [...commonTabFields, ...designTabFields, 'salespersonName', 'salespersonId',
        'alternateSalespersonName', 'alternateSalespersonId', 'baseDesignId', 'baseDesignName', 'images']),
      commonDetails: {
        ...pick(dto, commonTabFields),
        salesperson         : dto.salespersonId ? { id: dto.salespersonId, name: dto.salespersonName } : undefined,
        alternateSalesperson: dto.alternateSalespersonId ?
          { id: dto.alternateSalespersonId, name: dto.alternateSalespersonName } : undefined,
      },
      designDetails: {
        ...pick(dto, designTabFields),
        baseDesign: dto.baseDesignId ? { id: dto.baseDesignId, name: dto.baseDesignName } : undefined,
        images    : dto.images.filter((item) => item.type === IMAGE_TYPES.image),
        facades   : sortByStringField(
          dto.images.filter((item) => item.type === IMAGE_TYPES.facade && !!item.name), 'name'),
        floorPlans: dto.images.filter((item) => item.type === IMAGE_TYPES.floorPlan),
      },
    } as HlpDetails;
  }

  hlpDetailsToDto(details: HlpDetails): HlpDetailsDto {
    return {
      ...omit(details, 'commonDetails', 'designDetails', 'quoteHasChanges'),
      ...omit(details.commonDetails, 'salesperson', 'alternateSalesperson'),
      ...omit(details.designDetails, 'baseDesign', 'images', 'facades', 'floorPlans', 'websiteDescriptionText'),
      salespersonId           : details.commonDetails.salesperson?.id,
      salespersonName         : details.commonDetails.salesperson?.name,
      alternateSalespersonId  : details.commonDetails.alternateSalesperson?.id,
      alternateSalespersonName: details.commonDetails.alternateSalesperson?.name,
      baseDesignId            : details.designDetails.baseDesign?.id,
      baseDesignName          : details.designDetails.baseDesign?.name,
      images                  : [...details.designDetails.images, ...details.designDetails.facades, ...details.designDetails.floorPlans],
      isDisplayPriceOverridden: details.isDisplayPriceOverridden,
    } as HlpDetailsDto;
  }
}
